import React, { useState } from 'react';
import { InputGroup, Button } from 'react-bootstrap';
import { DatePicker } from '@material-ui/pickers';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { format } from 'date-fns';
import './MaterialSelect.css';

const bootstrapTheme = createMuiTheme({
  palette: {
    primary: {
      main: 'rgb(60, 141, 188)',
    },
  },
  overrides: {
    MuiInputBase: {
      input: {
        padding: '0.85rem 0.75rem 0.5rem',
        fontSize: '0.875rem',
        lineHeight: '1.3',
        color: '#495057',
        backgroundColor: '#fff',
        border: '1px solid #ced4da',
        borderRadius: '0.25rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        height: 'calc(2.6rem - 2px)',
        '&:focus': {
          borderColor: 'rgb(60, 141, 188)',
          boxShadow: '0 0 0 0.2rem rgba(60, 141, 188, 0.25)',
          backgroundColor: '#fff',
        }
      }
    },
    MuiInputLabel: {
      root: {
        fontSize: '0.75rem',
        fontFamily: 'inherit',
        color: 'rgb(60, 141, 188)',
        fontWeight: 500,
        '&.Mui-focused': {
          color: 'rgb(60, 141, 188)',
          fontWeight: 600,
        }
      }
    },
    MuiOutlinedInput: {
      root: {
        '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
          borderColor: '#ced4da',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgb(60, 141, 188)',
          borderWidth: '1px',
        }
      },
      notchedOutline: {
        borderColor: '#ced4da',
      }
    },
    MuiButton: {
      root: {
        fontFamily: 'inherit',
      }
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: 'rgb(60, 141, 188)',
      }
    },
    MuiPickersDay: {
      day: {
        color: '#495057',
        '&:hover': {
          backgroundColor: '#f8f9fa',
        },
      },
      daySelected: {
        backgroundColor: 'rgb(60, 141, 188)',
        color: '#fff',
        '&:hover': {
          backgroundColor: 'rgb(48, 113, 150)',
        },
      },
    }
  }
});

const MaterialDateInput = ({ value, onChange, label = "Fecha", width = "auto", required = false }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [focused, setFocused] = useState(false);

  const wrapperStyle = {
    width: width
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const formattedDate = format(date, 'dd/MM/yyyy');
      onChange(formattedDate);
    } else {
      onChange('');
    }
    setShowPicker(false);
  };

  const parseDateString = (dateString) => {
    if (!dateString) return null;
    try {
      const [day, month, year] = dateString.split('/').map(Number);
      return new Date(year, month - 1, day);
    } catch (error) {
      return null;
    }
  };

  return (
    <div 
      className={`material-select-wrapper ${(focused || value) ? 'focused' : ''}`}
      style={wrapperStyle}
    >
      <label className="material-select-label">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>
      <ThemeProvider theme={bootstrapTheme}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
          <InputGroup style={{maxWidth: width}}>
            <DatePicker
              value={parseDateString(value) || selectedDate}
              onChange={handleDateChange}
              format="dd/MM/yyyy"
              open={showPicker}
              onOpen={() => {
                setShowPicker(true);
                setFocused(true);
              }}
              onClose={() => {
                setShowPicker(false);
                setFocused(false);
              }}
              TextFieldComponent={({error, helperText, InputProps, ...props}) => (
                <input
                  {...props}
                  type="text"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  onFocus={() => setFocused(true)}
                  onBlur={() => {
                    if (!showPicker) setFocused(false);
                  }}
                  placeholder="dd/mm/yyyy"
                  className="form-control material-select"
                  style={{
                    borderRight: 'none',
                    borderTopRightRadius: '0',
                    borderBottomRightRadius: '0'
                  }}
                />
              )}
            />
            <InputGroup.Append>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowPicker(!showPicker)}
                style={{
                  border: '1px solid #ced4da',
                  borderLeft: 'none',
                  padding: '0em',
                  backgroundColor: 'white'
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                  if (!showPicker) setFocused(false);
                }}
              >
                📅
              </Button>
            </InputGroup.Append>
          </InputGroup>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </div>
  );
};

export default MaterialDateInput;